/**
 * SaveBar - Absolute positioned save bar component for form changes.
 *
 * Overlays at the bottom of its container when there are unsaved changes.
 * Does not affect parent container height. Requires parent to have padding-bottom.
 * Provides save and cancel buttons with loading and error states.
 */

import { AlertCircle } from '@untitledui/icons';
import React from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

export interface SaveBarProps {
  /** Number of unsaved changes to display */
  changeCount: number;
  /** Callback when save button is clicked */
  onSave: () => void;
  /** Callback when cancel button is clicked */
  onCancel: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Error message to display (if any) */
  error?: string | null;
  /** When true, the Save button is blocked due to unresolved conflicts. */
  blockedByConflicts?: boolean;
  /** Human-readable reason the save is blocked (shown when blockedByConflicts is true). */
  blockedReason?: string;
  /** Human-readable labels for the changed fields. When provided, hovering the
   *  change-count label reveals them in a tooltip. */
  changedFieldLabels?: string[];
}

/**
 * SaveBar component with absolute positioning at bottom of its container.
 * Overlays content without affecting parent container height.
 *
 * Only renders when changeCount > 0.
 */
export const SaveBar: React.FC<SaveBarProps> = ({
  changeCount,
  onSave,
  onCancel,
  isSaving = false,
  error = null,
  blockedByConflicts = false,
  blockedReason,
  changedFieldLabels,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);

  // Handle visibility state for animations
  React.useEffect(() => {
    if (changeCount > 0) {
      setShouldRender(true);
      // Small delay to ensure mount happens before animation starts
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (shouldRender) {
      // Start exit animation only if we were rendered
      setIsVisible(false);
      // Unmount after animation completes (300ms)
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [changeCount, shouldRender]);

  // Cmd/Ctrl+Enter saves, matching the memo composer (record/memo/MemoForm.tsx).
  // Bound to the document rather than the bar because focus is normally inside the
  // property input the user is editing, not on the bar itself. The listener only
  // exists while there are unsaved changes, so it can't fire outside edit mode.
  //
  // Both metaKey and ctrlKey are accepted (not getModifierKey) so the chord works on
  // either platform, mirroring the note composer's precedent.
  React.useEffect(() => {
    if (changeCount === 0 || isSaving || blockedByConflicts) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) {
        return;
      }
      // A rich-text editor (Plate) binds this chord itself — don't steal it.
      // Plain inputs/selects/textareas do NOT, and are exactly where the user
      // is typing when they want to save, so those must fall through.
      const active = document.activeElement as HTMLElement | null;
      if (active?.isContentEditable) {
        return;
      }
      e.preventDefault();
      onSave();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [changeCount, isSaving, blockedByConflicts, onSave]);

  // Don't render at all if shouldn't be rendered
  if (!shouldRender) {
    return null;
  }

  // Short label: the bar itself signals "unsaved" by its presence + the
  // brand-coloured Save button, so we don't need to repeat "unsaved" in
  // text. Keeping it short lets the bar fit in narrow containers (e.g. the
  // full-page view's 340px left column) without truncating.
  const changeLabel = changeCount === 1 ? '1 change' : `${changeCount} changes`;

  return (
    <div
      className={`absolute bottom-[calc(1.5rem_+_env(safe-area-inset-bottom))] left-6 right-6 z-50 border border-secondary bg-primary rounded-[10px] shadow-md transition-all duration-300 ease-in-out ${
        isVisible
          ? 'opacity-100 scale-100 translate-y-0'
          : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
      }`}
      role='status'
      aria-live='polite'
    >
      {/*
        Inner padding is intentionally compact (px-3 py-2) so the bar fits
        cleanly even in narrow containers (e.g. the full-page view's 340px
        left column ≈ 292px bar width after `left-6 right-6` gutters).
        Removed the previous `mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8`
        — that was sized for full-viewport use and caused the change-count
        label to wrap to 3 lines in the narrow column.

        Layout: label is `min-w-0 truncate` so it shrinks/clips before the
        buttons; buttons are `shrink-0` so they always stay legible. Result
        is "1 unsaved chan…" + Cancel + Save in extremely narrow containers,
        and the full "1 unsaved change" + buttons everywhere else.
      */}
      <div className='px-3 py-2'>
        <div className='flex items-center justify-between gap-2'>
          {/* Left: change count + optional error. min-w-0 lets the text
              truncate when the container is too narrow; without it flex
              children refuse to shrink below their content width and force
              the buttons off-screen. */}
          <div className='flex min-w-0 items-center gap-2'>
            {changedFieldLabels && changedFieldLabels.length > 0 ? (
              <Tooltip
                title={changedFieldLabels.join(' · ')}
                placement='top start'
              >
                <TooltipTrigger className='truncate text-xs md:text-sm font-medium text-secondary'>
                  {changeLabel}
                </TooltipTrigger>
              </Tooltip>
            ) : (
              <span className='truncate text-xs md:text-sm font-medium text-secondary'>
                {changeLabel}
              </span>
            )}
            {error && (
              <span className='flex shrink-0 items-center gap-1.5 text-xs md:text-sm text-utility-error-600'>
                <AlertCircle className='h-4 w-4' />
                <span className='truncate'>{error}</span>
              </span>
            )}
            {blockedByConflicts && blockedReason && !error && (
              <span
                id='save-bar-blocked-reason'
                className='truncate text-xs md:text-sm text-utility-warning-600'
                role='status'
                aria-live='polite'
              >
                {blockedReason}
              </span>
            )}
          </div>

          {/* Right: actions. shrink-0 prevents the buttons from collapsing
              when the container is narrow. */}
          <div className='flex shrink-0 items-center gap-1.5'>
            <Button
              type='button'
              color='secondary'
              size='sm'
              onClick={onCancel}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type='button'
              color='primary'
              size='sm'
              onClick={onSave}
              isDisabled={isSaving || blockedByConflicts}
              isLoading={isSaving}
              aria-keyshortcuts='Meta+Enter Control+Enter'
              aria-disabled={blockedByConflicts || undefined}
              aria-describedby={
                blockedByConflicts && blockedReason
                  ? 'save-bar-blocked-reason'
                  : undefined
              }
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
